import { Button } from "@nimit9/signet-ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@nimit9/signet-ui/components/dialog"
import { useQueryClient } from "@tanstack/react-query"
import { CheckCircle2Icon, CopyIcon, KeyRoundIcon, Loader2Icon } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useApiTokens, useCreateApiToken } from "@/hooks/use-api-tokens"

const MCP_PACKAGE = "paisa-mcp"
// Injected at build time from packages/mcp/package.json (vite.config.ts
// `define`), so the pinned version can't drift from the one the repo ships.
// Falls back to "latest" where no build step ran (bun test).
export const MCP_VERSION =
  typeof __PAISA_MCP_VERSION__ === "string" ? __PAISA_MCP_VERSION__ : "latest"

export interface AgentClient {
  id: "claude-code" | "claude-desktop" | "cursor" | "codex" | "vscode" | "windsurf"
  label: string
  /** Where to put the snippet, shown above it. */
  where: string
  snippet: (origin: string, token: string) => string
}

const pkg = () => `${MCP_PACKAGE}@${MCP_VERSION}`
const mcpServersJson = (origin: string, token: string) =>
  JSON.stringify(
    {
      mcpServers: {
        paisa: {
          command: "npx",
          args: ["-y", pkg()],
          env: { PAISA_API_URL: origin, PAISA_API_TOKEN: token },
        },
      },
    },
    null,
    2,
  )

/** Every MCP client Paisa documents a setup for (UAT J10-01: not just Claude). */
export const AGENT_CLIENTS: AgentClient[] = [
  {
    id: "claude-code",
    label: "Claude Code",
    where: "Run once in a terminal",
    snippet: (o, t) =>
      `claude mcp add paisa --env PAISA_API_URL=${o} --env PAISA_API_TOKEN=${t} -- npx -y ${pkg()}`,
  },
  {
    id: "claude-desktop",
    label: "Claude Desktop",
    where: "Settings → Developer → Edit config (claude_desktop_config.json), then restart",
    snippet: mcpServersJson,
  },
  {
    id: "cursor",
    label: "Cursor",
    where: "Add to ~/.cursor/mcp.json",
    snippet: mcpServersJson,
  },
  {
    id: "codex",
    label: "Codex",
    where: "Add to ~/.codex/config.toml",
    snippet: (o, t) =>
      `[mcp_servers.paisa]\ncommand = "npx"\nargs = ["-y", "${pkg()}"]\nenv = { PAISA_API_URL = "${o}", PAISA_API_TOKEN = "${t}" }`,
  },
  {
    id: "vscode",
    label: "VS Code",
    where: "Add to .vscode/mcp.json (Copilot agent mode)",
    snippet: (o, t) =>
      JSON.stringify(
        {
          servers: {
            paisa: {
              type: "stdio",
              command: "npx",
              args: ["-y", pkg()],
              env: { PAISA_API_URL: o, PAISA_API_TOKEN: t },
            },
          },
        },
        null,
        2,
      ),
  },
  {
    id: "windsurf",
    label: "Windsurf",
    where: "Add to ~/.codeium/windsurf/mcp_config.json",
    snippet: mcpServersJson,
  },
]

interface ConnectClaudeDialogProps {
  open: boolean
  onClose: () => void
  /** Shown above the config block — lets each call site explain why it landed here. */
  context?: string
}

/**
 * The "hand off to Claude" screen: on explicit request, mints an API token
 * and shows a single copy-paste line to wire up the MCP server. This is the
 * ONLY ingestion path for anything Paisa doesn't parse in the browser (PDFs,
 * other banks, ad-hoc questions) — CLAUDE.md's "no in-app AI" rule means
 * Paisa never parses those itself, it always hands off here.
 *
 * The token is minted only when the person clicks "Generate token" — NOT on
 * open. This dialog opens from a drag-and-drop handler (a stray non-CSV file
 * dropped anywhere in the app), so auto-minting on open would silently
 * create a live, never-expiring, full-access credential nobody asked for and
 * that nothing ever cleans up (H3).
 */
export function ConnectClaudeDialog({ open, onClose, context }: ConnectClaudeDialogProps) {
  const createToken = useCreateApiToken()
  const { data: tokensData } = useApiTokens()
  const qc = useQueryClient()

  const [rawToken, setRawToken] = useState<string | null>(null)
  const [tokenId, setTokenId] = useState<string | null>(null)
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle")
  const commandRef = useRef<HTMLElement>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setRawToken(null)
      setTokenId(null)
      setCopyState("idle")
      setError(null)
    }
  }, [open])

  function generateToken() {
    setError(null)
    createToken.mutate("Connect Claude (MCP)", {
      onSuccess: (res) => {
        setRawToken(res.token)
        setTokenId(res.id)
      },
      onError: (e) => setError(e instanceof Error ? e.message : "Couldn't create a token"),
    })
  }

  // Cheap "waiting for first call" indicator: the token list already carries
  // lastUsedAt, so poll it while this dialog is open instead of adding new
  // server surface just to detect a first MCP call.
  const connected =
    !!tokenId && tokensData?.data.some((t) => t.id === tokenId && t.lastUsedAt !== null)

  useEffect(() => {
    if (!open || !tokenId || connected) return
    const id = setInterval(() => qc.invalidateQueries({ queryKey: ["api-tokens"] }), 4000)
    return () => clearInterval(id)
  }, [open, tokenId, connected, qc])

  const origin = typeof window !== "undefined" ? window.location.origin : ""
  const [clientId, setClientId] = useState<AgentClient["id"]>("claude-code")
  const client = AGENT_CLIENTS.find((c) => c.id === clientId) ?? AGENT_CLIENTS[0]
  const command = rawToken ? client.snippet(origin, rawToken) : ""

  // Select the command so a manual Cmd/Ctrl+C works when the Clipboard API
  // is missing (non-secure context) or refuses (permissions).
  function selectCommand() {
    const node = commandRef.current
    const selection = typeof window !== "undefined" ? window.getSelection() : null
    if (!node || !selection) return
    const range = document.createRange()
    range.selectNodeContents(node)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  async function copyCommand() {
    if (!command) return
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable")
      await navigator.clipboard.writeText(command)
      setCopyState("copied")
      setTimeout(() => setCopyState("idle"), 2000)
    } catch {
      setCopyState("failed")
      selectCommand()
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Connect an AI assistant</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          {context && <p className="text-body text-text-secondary">{context}</p>}
          <p className="text-body text-text-secondary">
            Claude, Cursor, Codex and other MCP apps can read statements Paisa can't parse itself,
            answer questions and tidy categories, always through a preview you approve. The app
            never runs AI on your data itself.
          </p>

          {error && <p className="text-caption text-accent-red">{error}</p>}

          {!rawToken && (
            <Button onClick={generateToken} disabled={createToken.isPending} className="gap-1.5">
              {createToken.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <KeyRoundIcon className="size-4" />
                  Generate token
                </>
              )}
            </Button>
          )}

          {rawToken && (
            <>
              <div className="space-y-1.5">
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Your AI app">
                  {AGENT_CLIENTS.map((c) => (
                    <Button
                      key={c.id}
                      size="sm"
                      variant={c.id === clientId ? "default" : "outline"}
                      aria-pressed={c.id === clientId}
                      onClick={() => {
                        setClientId(c.id)
                        setCopyState("idle")
                      }}
                    >
                      {c.label}
                    </Button>
                  ))}
                </div>
                <p className="text-label uppercase tracking-wider text-text-muted">
                  {client.where}
                </p>
                <div className="flex items-start gap-2 rounded-lg border border-border-subtle bg-bg-base px-3 py-2.5">
                  <code
                    ref={commandRef}
                    className="flex-1 select-all whitespace-pre-wrap break-all font-mono text-caption text-accent-green"
                  >
                    {command}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyCommand}
                    className="shrink-0 gap-1.5"
                  >
                    <CopyIcon className="size-3.5" />
                    {copyState === "copied"
                      ? "Copied"
                      : copyState === "failed"
                        ? "Copy failed"
                        : "Copy"}
                  </Button>
                </div>
                {copyState === "failed" && (
                  <p role="alert" className="text-caption text-text-secondary">
                    Copy failed; select the text above and copy it yourself (it's selected for you).
                  </p>
                )}
                <p className="text-caption text-text-muted">
                  The token is shown once and works like a password for your Paisa data. Revoke it
                  any time in Settings → API tokens.
                </p>
              </div>

              <div
                className="flex items-center gap-2 rounded-lg border px-3 py-2.5 text-caption"
                style={
                  connected
                    ? { borderColor: "var(--accent-green)", background: "rgba(52,211,153,0.08)" }
                    : { borderColor: "var(--border-subtle)", background: "var(--bg-elevated)" }
                }
              >
                {connected ? (
                  <>
                    <CheckCircle2Icon className="size-4 shrink-0 text-accent-green" />
                    <span className="font-medium text-accent-green">
                      Connected — your assistant just reached your data.
                    </span>
                  </>
                ) : (
                  <>
                    <Loader2Icon className="size-4 shrink-0 animate-spin text-text-muted" />
                    <span className="text-text-muted">Waiting for the first MCP call…</span>
                  </>
                )}
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose}>
              {connected ? "Done" : rawToken ? "I'll do this later" : "Cancel"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
